/* eslint no-param-reassign: 0 */
import { ItemTypeConsts, SettingsConsts, StoreModuleMap } from 'client/util/Constants';
import {
  cloneDeep,
  isEqual,
  isMatch,
  omit,
} from 'lodash';
import DirectoryApi from 'client/api/v2/DirectoryApi';
import GSuiteApi from 'client/api/v2/GSuiteApi';
import LdapApi from 'client/api/v2/LdapServerApi';
import O365Api from 'client/api/v2/O365Api';
import OrganizationsApi from 'client/api/v2/OrganizationsApi';
import RadiusServerApi from 'client/api/v2/RadiusServerApi';
import SettingsApi from 'client/api/v2/SettingsApi';
import Vue from 'vue';
import createModel from 'client/store/modules/models/createModel';

const settingsModule = StoreModuleMap[ItemTypeConsts.settings];
const { sessionDurationDefault } = SettingsConsts;

// Private Helper methods
/**
 * @description Return an object with general settings and security changes that are not
 * related to any of the directories or radius. This is needed to determine what needs
 * to be saved by the SETTINGS api. The directory and radius are handled by those APIs respectively.
 */
const getSettingsSpecificChanges = item => omit(item, [
  'directories',
  'gSuiteServers',
  'ldapServer',
  'o365Servers',
  'orgId',
  'radiusServers',
]);

/**
 * @description This model represents the information that can be configured
 * on the General and Security Settings pages. If new features are added then
 * add corresponding fields/objects/arrays here.
 */
export const getDefaultItem = () => ({
  contactEmail: '',
  contactName: '',
  directories: {},
  disableGoogleLogin: false,
  disableUM: false,
  displayPreferences: {
    orgInsights: {
      deviceNotifications: {
        agentOutOfDateMetric: { visible: false },
        inactiveMetric: { visible: false },
        uptimeMetric: { visible: false },
        withoutPoliciesMetric: { visible: false },
        withoutUsersMetric: { visible: false },
      },
      diskEncryption: {},
      expiredPasswords: {},
      fundamentals: {},
      jcUniversity: {},
      learningCenter: {},
      officeHours: {},
      upcomingPasswordExpiration: {},
      userLockouts: {},
      mdmCertificateExpirations: {},
      newOSReleases: {},
      newUsers: {},
      consoleStats: {
        totalUsers: { visible: false },
        totalDevices: { visible: false },
        totalGroups: { visible: false },
        totalApplications: { visible: false },
        totalConfigurations: { visible: false },

      },
      userNotifications: {
        usersWithAdminSudoAccess: { visible: false },
        userWithAdminSudoPasswordlessAccess: { visible: false },
        usersWithoutPassword: { visible: false },
        usersWithSambaAccess: { visible: false },
        usersWithoutDevices: { visible: false },
      },
    },
  },
  emailDisclaimer: '',
  enableManagedUID: false,
  enableUserPortalAgentInstall: false,
  features: {},
  gSuiteServers: [],
  ldapServer: {},
  name: '',
  o365Servers: [],
  orgId: undefined,
  passwordCompliance: undefined,
  passwordPolicy: undefined,
  pendingDelete: false,
  radiusServers: [],
  sessionDuration: sessionDurationDefault,
  systemUserPasswordExpirationInDays: 0,
  systemUsersCanEdit: false,
  userPortal: {},
  windowsMDM: {
    enabled: false,
    autoEnroll: false,
  },
  deleteKey: undefined,
});

const model = createModel(getDefaultItem, '_id', SettingsApi);

export default {
  namespaced: true,

  state: {
    ...model.state,
  },

  actions: {
    ...model.actions,

    fetchItem({ commit, dispatch, rootGetters }) {
      return SettingsApi
        .getGlobalSettings()
        .then((item) => {
          // Save the orgId for use in fetching and saving
          commit('SET_ITEM', { orgId: item.ORG_ID });
          commit('SET_PREVIOUS_ITEM', { orgId: item.ORG_ID });
          return item;
        })
        // Fetch additional v2 settings
        .then(async (item) => {
          // TODO: Remove FF check when JumpCloud GO is ready
          if (rootGetters['FeatureFlagsModule/betaFeatures'].jumpcloudGo) {
            await dispatch('JumpcloudGoModule/fetchSettings', {}, { root: true });
            item.SETTINGS.features.jcGo = rootGetters['JumpcloudGoModule/getFeatureSettings'];
          }
          return item;
        })
        // Clone the item so that any changes to non-primitive type values,
        // such as an array of phone numbers, are _not_ accidentally made
        // to the previous item when the item is mutated.
        .then((item) => {
          commit('SET_ITEM', cloneDeep(item.SETTINGS));
          commit('SET_PREVIOUS_ITEM', cloneDeep(item.SETTINGS));
        })
        .then(() => {
          // TODO: Remove FF check when JumpCloud GO is ready
          if (rootGetters['FeatureFlagsModule/betaFeatures'].jumpcloudGo) {
            dispatch('JumpcloudGoModule/fetchPolicies', {}, { root: true });
          }
        })
        // Get the organization logo before rendering the settings page
        .then(() => dispatch('fetchOrganizationLogo'))
        .then(() => dispatch('fetchSessionDuration'))
        .then(() => {
          dispatch('fetchDirectoryInformation');
          dispatch('fetchRadiusServers');
        });
    },

    fetchDirectoryInformation(context) {
      DirectoryApi
        .fetchAll()
        .then((directories) => {
          const gSuiteTasks = [];
          const o365Tasks = [];

          directories.forEach(async (directory) => {
            if (directory.type === 'g_suite') {
              gSuiteTasks.push(GSuiteApi.get(directory.id));
            }

            if (directory.type === 'ldap_server') {
              LdapApi.get(directory.id).then((ldap) => {
                context.commit('SET_ITEM', { ldapServer: cloneDeep(ldap) });
                context.commit('SET_PREVIOUS_ITEM', { ldapServer: cloneDeep(ldap) });
              });
            }

            if (directory.type === 'office_365') {
              o365Tasks.push(O365Api.get(directory.id));
            }
          });

          if (gSuiteTasks.length > 0) {
            Promise.all(gSuiteTasks).then((gSuiteServers) => {
              context.commit('SET_ITEM', { gSuiteServers: cloneDeep(gSuiteServers) });
              context.commit('SET_PREVIOUS_ITEM', { gSuiteServers: cloneDeep(gSuiteServers) });
            });
          }

          if (o365Tasks.length > 0) {
            Promise.all(o365Tasks).then((o365Servers) => {
              context.commit('SET_ITEM', { o365Servers: cloneDeep(o365Servers) });
              context.commit('SET_PREVIOUS_ITEM', { o365Servers: cloneDeep(o365Servers) });
            });
          }
        })
        .catch(() => { /* NOOP */ });
    },

    fetchOrganizationLogo(context) {
      const { orgId } = context.state.item;
      return OrganizationsApi
        .getOrganizationLogoUrl(orgId)
        .then((logoUrl) => {
          // The response will be null if the org doesn't have a logo set
          if (logoUrl) {
            context.commit('SET_ITEM', { logoUrl });
            context.commit('SET_PREVIOUS_ITEM', { logoUrl });
          }
        })
        .catch(() => { /* NOOP */ });
    },

    fetchRadiusServers(context) {
      RadiusServerApi
        .fetchAll()
        .then((radiusServers) => {
          context.commit('SET_ITEM', { radiusServers: cloneDeep(radiusServers) });
          context.commit('SET_PREVIOUS_ITEM', { radiusServers: cloneDeep(radiusServers) });
        })
        .catch(() => { /* NOOP */ });
    },

    fetchSessionDuration({ commit, state }) {
      const { idleSessionDurationMinutes } = state.item.userPortal;

      const minutesInDays = 60 * 24;
      const sessionDuration = {
        days: Math.floor(idleSessionDurationMinutes / minutesInDays),
        hours: Math.floor((idleSessionDurationMinutes % minutesInDays) / 60),
        minutes: idleSessionDurationMinutes % 60,
      };

      commit('SET_ITEM', { sessionDuration });
      commit('SET_PREVIOUS_ITEM', { sessionDuration });
    },

    /**
     * @description Make sure each type of settings item is saved.  There are several Apis that
     * must be called to accomplish this. Each directory or server has its own Api; the same is
     * true for the organization password policy. Only make the relevant Api call when the item
     * has changed.
     *
     * Note: `passwordCompliance` is set to `custom` for all _save_ actions (this is a
     * legacy item field)
     */
    async saveItem(context) {
      const { item, previousItem } = context.state;
      const {
        gSuiteServers,
        ldapServer,
        o365Servers,
        orgId,
        radiusServers,
        features,
      } = item;

      const tasks = [];

      // TODO: Remove FF check when JumpCloud GO is ready
      if (context.rootGetters['FeatureFlagsModule/betaFeatures'].jumpcloudGo) {
        const isJcGoEnabled = Object.keys(features).filter(key => key === 'jcGo').length;

        if (isJcGoEnabled && !isEqual(features.jcGo, previousItem.features.jcGo)) {
          tasks.push(context.dispatch('JumpcloudGoModule/updateSettings', features.jcGo, { root: true }));
        }
      }

      gSuiteServers.forEach((gSuite, index) => {
        if (!isEqual(gSuite, previousItem.gSuiteServers[index])) {
          tasks.push(GSuiteApi.patch(
            gSuite.id,
            {
              userLockoutAction: gSuite.userLockoutAction,
              userPasswordExpirationAction: gSuite.userPasswordExpirationAction,
            },
          ));
        }
      });

      if (!isEqual(ldapServer, previousItem.ldapServer)) {
        tasks.push(LdapApi.patch(
          ldapServer.id,
          {
            userLockoutAction: ldapServer.userLockoutAction,
            userPasswordExpirationAction: ldapServer.userPasswordExpirationAction,
          },
        ));
      }

      o365Servers.forEach((o365, index) => {
        if (!isEqual(o365, previousItem.o365Servers[index])) {
          tasks.push(O365Api.patch(
            o365.id,
            {
              userLockoutAction: o365.userLockoutAction,
              userPasswordExpirationAction: o365.userPasswordExpirationAction,
            },
          ));
        }
      });

      radiusServers.forEach((server, index) => {
        if (!isEqual(server, previousItem.radiusServers[index])) {
          tasks.push(RadiusServerApi.update(server));
        }
      });

      // windows MDM enrollment will be disabled if the user portal enrollment option is disabled
      const shouldDisableWindowsMdm = item.enableUserPortalAgentInstall === false
        && previousItem.enableUserPortalAgentInstall === true
        && item.windowsMDM.enabled === true;
      // Get any changes specific to the SETTINGS/Organizations apis
      const settingUpdates = shouldDisableWindowsMdm
        ? {
          ...getSettingsSpecificChanges(item),
          windowsMDM: {
            enabled: false,
            autoEnroll: item.windowsMDM.autoEnroll,
          },
        }
        : {
          ...getSettingsSpecificChanges(item),
        };

      if (!isMatch(previousItem, settingUpdates)) {
        tasks.push(OrganizationsApi.updateOrganizationSettings(orgId, {
          settings: settingUpdates,
        }));
      }

      await Promise.all(tasks);

      if (tasks.length) {
        const logoChanged = typeof item.logo !== 'undefined';

        // Don't persist the logo field - it's only needed for the save
        if (logoChanged) delete item.logo;

        const updatedItem = cloneDeep(item);
        if (shouldDisableWindowsMdm) {
          updatedItem.windowsMDM.enabled = false;
        }

        context.commit('SET_ITEM', cloneDeep(updatedItem));
        context.commit('SET_PREVIOUS_ITEM', cloneDeep(updatedItem));

        // Re-fetch the organization logo after the save to get the updated logoUrl
        if (logoChanged) {
          await context.dispatch('fetchOrganizationLogo');
        }

        // Ensure that the item is updated in the Settings View by passing the
        // item to the SettingsModule so it can be synced in the store immediately.
        context.dispatch(`${settingsModule}/updateSettings`, updatedItem, { root: true });

        // Delete gracePeriodDate so it's not used on further requests unless set again
        context.commit('DELETE_PASSWORD_POLICY_FIELDS', { keysToDelete: ['gracePeriodDate'], deletePreviousItemField: true });
      }
    },

    async requestDeleteOrganization({ commit, state }) {
      const { orgId } = state.item;
      const pendingDelete = true;

      await OrganizationsApi.requestDeleteOrganization(orgId);

      commit('SET_ITEM', { pendingDelete });
      commit('SET_PREVIOUS_ITEM', { pendingDelete });
    },

    async autoDeleteOrganization(
      { commit, state }, { isProviderAdmin, redirectTarget, resendOrgDeleteKey },
    ) {
      const { orgId: id, deleteKey, name } = state.item;

      const res = await OrganizationsApi.autoDeleteOrganization({
        id, name, deleteKey, isProviderAdmin, redirectTarget, resendOrgDeleteKey,
      });

      const newDeleteKey = res.headers['x-org-delete-key'];

      commit('SET_ITEM', { deleteKey: newDeleteKey });
      commit('SET_PREVIOUS_ITEM', { deleteKey: newDeleteKey });
    },

    setDeviceNotificationMetricVisibility({ commit }, { widgetType, visible }) {
      commit('SET_DEVICE_NOTIFICATION_METRIC_VISIBILITY', { widgetType, visible });
    },
    setConsoleStatsVisibility({ commit }, { widgetType, visible }) {
      commit('SET_CONSOLE_STATS_VISIBILITY', { widgetType, visible });
    },

    setUserNotificationMetricVisibility({ commit }, { widgetType, visible }) {
      commit('SET_USER_NOTIFICATION_METRIC_VISIBILITY', { widgetType, visible });
    },

    setHomeWidgetVisibility({ commit }, { widgetType, visible }) {
      commit('SET_HOME_WIDGET_VISIBILITY', { widgetType, visible });
    },

    updatePasswordConfig(context, payload) {
      context.commit('UPDATE_PASSWORD_CONFIG', payload);
    },

    updatePasswordPolicy(context, payload) {
      context.commit('UPDATE_PASSWORD_POLICY', payload);
    },

    deletePasswordPolicyFields(context, payload) {
      context.commit('DELETE_PASSWORD_POLICY_FIELDS', payload);
    },
  },

  getters: {
    ...model.getters,
  },

  mutations: {
    ...model.mutations,

    /**
     * @description Allows the ability to set an individual password config field
     * instead of having to pass the entire object
     */
    UPDATE_PASSWORD_CONFIG(state = { item: {} }, payload = {}) {
      const {
        modelName,
        id,
        value,
        key,
      } = payload;

      if (Array.isArray(state.item[modelName])) {
        const index = state.item[modelName].findIndex(x => x.id === id);
        if (index > -1) {
          state.item[modelName][index][key] = value;
        }
      } else {
        state.item[modelName][key] = value;
      }
    },

    /**
     * @description Allows the ability to easily set individual password policy fields
     * instead of having to pass the entire object
     */
    UPDATE_PASSWORD_POLICY(state = { item: {} }, payload = {}) {
      Object.keys(payload).forEach((key) => {
        Vue.set(state.item.passwordPolicy, key, payload[key]);
      });
    },

    /**
     * @description Allows the ability to delete fields of the password policy
     */
    DELETE_PASSWORD_POLICY_FIELDS(state = { item: {} }, payload = {}) {
      payload.keysToDelete.forEach((key) => {
        Vue.delete(state.item.passwordPolicy, key);
        if (payload.deletePreviousItemField) {
          Vue.delete(state.previousItem.passwordPolicy, key);
        }
      });
    },

    SET_DEVICE_NOTIFICATION_METRIC_VISIBILITY(state = { item: {} }, { widgetType, visible } = {}) {
      state.item.displayPreferences.orgInsights.deviceNotifications[widgetType].visible = visible;
    },
    SET_CONSOLE_STATS_VISIBILITY(state = { item: {} }, { widgetType, visible } = {}) {
      state.item.displayPreferences.orgInsights.consoleStats[widgetType].visible = visible;
    },

    SET_USER_NOTIFICATION_METRIC_VISIBILITY(state = { item: {} }, { widgetType, visible } = {}) {
      state.item.displayPreferences.orgInsights.userNotifications[widgetType].visible = visible;
    },

    SET_HOME_WIDGET_VISIBILITY(state = { item: {} }, { widgetType, visible } = {}) {
      state.item.displayPreferences.orgInsights[widgetType].visible = visible;
    },
  },
};
